/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltipInPortal, useTooltip } from "@visx/tooltip";
import { When } from "react-if";
import {
  BrickLoader,
  UniversalWrap,
  getColorsPalette,
  getTableColumns,
  mapAccumulatedCostChartColors,
  StackBarChart,
  tooltipStyles,
  getEndDate,
  ChartType,
  CostAnalysisFields,
  Granularity,
  Period,
} from "~/components";
import { useCurrency } from "~/hooks";
import { ApplicationCostTableData } from "../ApplicationCostTableData";
import { useApplicationAccumulatedCostChart } from "../hooks";

type Props = {
  appId: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
};

export function ApplicationAccumulatedCostView({
  appId,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();

  const {
    accumulatedCostTableData,
    accumulatedCostTotal,
    accumulatedCostChartData,
    loading,
    error,
  } = useApplicationAccumulatedCostChart({
    appId,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const colors = mapAccumulatedCostChartColors([CostAnalysisFields.DAILY_COST]);

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: accumulatedCostTableData,
        hasFooter,
        period,
        chartType: ChartType.ACCUMULATED_COST,
        colorsPalette: getColorsPalette(ChartType.ACCUMULATED_COST),
      }),
    [startDate, endDate, currency, hasFooter, accumulatedCostTableData]
  );

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <ParentSize>
        {(parent) => (
          <>
            <StackBarChart
              width={parent.width}
              height={280}
              data={accumulatedCostChartData}
              granularity={granularity}
              loading={loading}
              startDate={startDate}
              endDate={endDate}
              period={period}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
              keys={[CostAnalysisFields.DAILY_COST]}
              colors={colors}
              chartType={ChartType.ACCUMULATED_COST}
              containerRef={containerRef}
            />

            <When condition={tooltipOpen}>
              <TooltipInPortal
                // set this to random so it correctly updates with parent bounds
                key={Date.now()}
                top={tooltipTop}
                left={tooltipLeft}
                style={tooltipStyles}
              >
                {tooltipData}
              </TooltipInPortal>
            </When>
          </>
        )}
      </ParentSize>
      <ApplicationCostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={accumulatedCostTableData}
        chartType={ChartType.ACCUMULATED_COST}
        total={accumulatedCostTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
      />
    </UniversalWrap>
  );
}
